
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useCreateEvent, useUpdateEvent } from "@/hooks/useEvents";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { CalendarIcon, X, CalendarDays, Users, Briefcase, ClipboardList, Puzzle, Plus, Search, Link, Trash2, Building, AlertTriangle, Video } from "lucide-react";
import { format, addMinutes } from "date-fns";
import { cn } from "@/lib/utils";
import { useModal } from "@/contexts/ModalContext";
import { useSettings } from "@/providers/SettingsProvider";
import { useCandidates } from "@/hooks/candidates/useCandidates";
import { useUsers } from "@/hooks/useUsers";
import { useEventLinkedUsers } from "@/hooks/useLinkedUsers";
import { Badge } from "@/components/ui/badge";
import { matchesCandidateSearch } from "@/utils/searchUtils";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { EventObject } from "./types";
import { formatCalendarDate } from "@/utils/dateFormatter";
import { EntitySearchPopover } from "./EntitySearchPopover";
import { SyncStatusBadge } from "./SyncStatusBadge";
import { AddCandidateSelectionModal } from "@/components/candidates/AddCandidateSelectionModal";
import { AddCandidateForm } from "@/components/candidates/AddCandidateForm";
import { AddCompanyForm } from "@/components/companies/AddCompanyForm";
import { JobOffersAddModal } from "@/components/jobs/JobOffersAddModal";
import { CandidateFormData } from "@/utils/candidate";
import { Candidate } from "@/types/candidate";
import { useAddCompany } from "@/hooks/useAddCompany";
import { useAddJob } from "@/hooks/jobs/useAddJob";
import { getResponseStatusColor, getResponseStatusLabel } from "@/hooks/useEventParticipants";

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
  onDelete?: () => void;
  initialDate?: Date;
  initialData?: EventObject;
  isEditing?: boolean;
  customHeaderActions?: React.ReactNode;
  isDeleting?: boolean;
}

const EVENT_COLORS = [
  { value: "#9b87f5", label: "Purple" },
  { value: "#1EAEDB", label: "Blue" },
  { value: "#F97316", label: "Orange" },
  { value: "#D946EF", label: "Pink" },
  { value: "#0EA5E9", label: "Sky" },
  { value: "#8E9196", label: "Gray" },
];

const USER_RESPONSE_STATUSES = [
  { value: "yes", label: "Accepted" },
  { value: "maybe", label: "Tentative" },
  { value: "no", label: "Declined" },
  { value: "noreply", label: "No Response" },
];

const EVENT_PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export const CreateEventModal = ({ isOpen, onClose, onSave, onDelete, initialDate, initialData, isEditing = false, customHeaderActions, isDeleting = false }: CreateEventModalProps) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const { calendarSettings, timezone } = useSettings();
  const createEventMutation = useCreateEvent();
  const updateEventMutation = useUpdateEvent();
  const queryClient = useQueryClient();
  const { candidates } = useCandidates();
  const { data: users = [] } = useUsers();
  const { data: dbLinkedUsers = [], isLoading: linkedUsersLoading } = useEventLinkedUsers(isEditing && initialData?.id ? initialData.id : undefined);
  const defaultDuration = calendarSettings?.defaultEventDuration || 60;
  const defaultColor = calendarSettings?.defaultEventColor || "#9b87f5";
  const userTimezone = timezone || 'UTC';

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    color: defaultColor,
    location: "",
    url: "",
    priority: "medium" as 'low' | 'medium' | 'high' | 'urgent',
    visibility: "private" as 'private' | 'public' | 'team',
    all_day: false,
    startDate: initialDate || new Date(),
    endDate: addMinutes(initialDate || new Date(), defaultDuration)
  });

  // Current user's response status (stored in event_users table)
  const [currentUserResponseStatus, setCurrentUserResponseStatus] = useState<'yes' | 'no' | 'maybe' | 'noreply'>('yes');

  const [relations, setRelations] = useState({
    candidates: [] as string[],
    offers: [] as string[],
    companies: [] as string[],
    matches: [] as string[],
    contacts: [] as string[]
  });

  const [linkedUsers, setLinkedUsers] = useState<string[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(false);

  // Contact and external email state
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [externalEmails, setExternalEmails] = useState<Array<{
    email: string;
    display_name: string;
    attendance_type: 'required' | 'optional' | 'resource';
  }>>([]);

  // Response status tracking for participants
  const [userResponseStatus, setUserResponseStatus] = useState<Record<string, 'yes' | 'no' | 'maybe' | 'noreply'>>({});
  const [contactResponseStatus, setContactResponseStatus] = useState<Record<string, 'yes' | 'no' | 'maybe' | 'noreply'>>({});
  const [externalEmailResponseStatus, setExternalEmailResponseStatus] = useState<Record<string, 'yes' | 'no' | 'maybe' | 'noreply'>>({});

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'custom-fields'>('general');
  const [mobileTab, setMobileTab] = useState<'general' | 'relations'>('general');
  
  // Custom fields state (for future implementation)
  const [fields] = useState<any[]>([]);
  
  // Candidate relations state
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const [candidateSearchResults, setCandidateSearchResults] = useState<Array<{id: string, name: string}>>([]);
  const [isCandidateSearching, setIsCandidateSearching] = useState(false);
  
  // Match relations state
  const [matchSearchQuery, setMatchSearchQuery] = useState('');
  const [matchSearchResults, setMatchSearchResults] = useState<Array<{id: string, name: string}>>([]);
  const [isMatchSearching, setIsMatchSearching] = useState(false);
  
  // Job relations state
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [jobSearchResults, setJobSearchResults] = useState<Array<{id: string, name: string}>>([]);
  const [isJobSearching, setIsJobSearching] = useState(false);
  
  // Company relations state
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState<Array<{id: string, name: string}>>([]);
  const [isCompanySearching, setIsCompanySearching] = useState(false);

  // Add entity modals
  const { addCompany } = useAddCompany();
  const { addJob } = useAddJob();
  const [isAddCandidateSelectionOpen, setIsAddCandidateSelectionOpen] = useState(false);
  const [isAddCandidateFormOpen, setIsAddCandidateFormOpen] = useState(false);
  const [candidatePrefillData, setCandidatePrefillData] = useState<CandidateFormData | null>(null);
  const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);
  const [isAddJobOpen, setIsAddJobOpen] = useState(false);

  // Contact state
  const [contacts, setContacts] = useState<Array<{id: string, first_name: string, last_name: string, email: string}>>([]);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState<Array<{id: string, name: string}>>([]);
  const [isContactSearching, setIsContactSearching] = useState(false);

  // External email autocomplete state
  const [previousExternalEmails, setPreviousExternalEmails] = useState<Array<{email: string, display_name: string | null}>>([]);
  const [emailSuggestions, setEmailSuggestions] = useState<Array<{email: string, display_name: string | null}>>([]);
  const [activeEmailIndex, setActiveEmailIndex] = useState<number>(-1);

  // Register modal with context
  useEffect(() => {
    if (isOpen) {
      openModal();
    } else {
      closeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Get current user on mount
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
    };
    getCurrentUser();
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isEditing && initialData) {
        // Edit mode - populate form with existing event data
        const hasUrl = !!(initialData.metadata?.url);
        setIsOnline(hasUrl);

        setFormData({
          title: initialData.title,
          description: initialData.description || "",
          color: initialData.color || defaultColor,
          location: initialData.metadata?.location || "",
          url: initialData.metadata?.url || "",
          priority: initialData.metadata?.priority || "medium",
          visibility: initialData.metadata?.visibility || "private",
          all_day: initialData.metadata?.all_day || false,
          startDate: new Date(initialData.start),
          endDate: new Date(initialData.end)
        });

        setRelations({
          candidates: Array.isArray(initialData.metadata?.candidates) ? [...initialData.metadata.candidates] : [],
          offers: Array.isArray(initialData.metadata?.offers) ? [...initialData.metadata.offers] : [],
          companies: Array.isArray(initialData.metadata?.companies) ? [...initialData.metadata.companies] : [],
          matches: Array.isArray(initialData.metadata?.matches) ? [...initialData.metadata.matches] : [],
          contacts: Array.isArray(initialData.metadata?.contacts) ? [...initialData.metadata.contacts] : []
        });

        // Fetch participants from the database (both internal users and external)
        const fetchParticipants = async () => {
          // Fetch internal linked users with response status
          const { data: eventUsers } = await supabase
            .from('event_users')
            .select('user_id, response_status')
            .eq('event_id', initialData.id);

          if (eventUsers) {
            const userIds = eventUsers.map(eu => eu.user_id);
            const statusMap: Record<string, 'yes' | 'no' | 'maybe' | 'noreply'> = {};
            eventUsers.forEach(eu => {
              statusMap[eu.user_id] = eu.response_status || 'noreply';
              // Set current user's response status
              if (currentUserId && eu.user_id === currentUserId) {
                setCurrentUserResponseStatus(eu.response_status || 'noreply');
              }
            });
            setLinkedUsers(userIds);
            setUserResponseStatus(statusMap);
          } else {
            setLinkedUsers([]);
            setUserResponseStatus({});
          }

          // Fetch external participants (contacts and guests)
          const { data: participants } = await supabase
            .from('event_participants')
            .select('*')
            .eq('event_id', initialData.id);

          if (participants) {
            // Separate contact-based and external participants
            const contactIds = participants
              .filter(p => p.contact_id !== null)
              .map(p => p.contact_id);

            const contactStatusMap: Record<string, 'yes' | 'no' | 'maybe' | 'noreply'> = {};
            participants
              .filter(p => p.contact_id !== null)
              .forEach(p => {
                contactStatusMap[p.contact_id] = p.response_status || 'noreply';
              });

            const externalGuests = participants
              .filter(p => p.contact_id === null && p.candidate_id === null)
              .map(p => ({
                email: p.email,
                display_name: p.display_name || '',
                attendance_type: p.attendance_type as 'required' | 'optional' | 'resource'
              }));

            const externalStatusMap: Record<string, 'yes' | 'no' | 'maybe' | 'noreply'> = {};
            participants
              .filter(p => p.contact_id === null && p.candidate_id === null)
              .forEach(p => {
                externalStatusMap[p.email] = p.response_status || 'noreply';
              });

            setSelectedContacts(contactIds);
            setContactResponseStatus(contactStatusMap);
            setExternalEmails(externalGuests);
            setExternalEmailResponseStatus(externalStatusMap);
          }
        };

        fetchParticipants();
      } else {
        // Create mode - use default values
        const start = initialDate ? new Date(initialDate) : new Date();
        const isAllDay = initialData?.metadata?.all_day || false;
        const end = isAllDay ? start : addMinutes(start, defaultDuration);

        // If all-day, set times to 00:00 and 23:59
        if (isAllDay) {
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 0, 0);
        }

        setIsOnline(false);
        setFormData({
          title: "",
          description: "",
          color: defaultColor,
          location: "",
          url: "",
          priority: "medium",
          visibility: "private",
          all_day: isAllDay,
          startDate: start,
          endDate: end
        });
        setCurrentUserResponseStatus('yes'); // Default to "Accepted" for new events

        // Use initial relations if provided, otherwise empty arrays
        setRelations({
          candidates: Array.isArray(initialData?.metadata?.candidates) ? [...initialData.metadata.candidates] : [],
          offers: Array.isArray(initialData?.metadata?.offers) ? [...initialData.metadata.offers] : [],
          companies: Array.isArray(initialData?.metadata?.companies) ? [...initialData.metadata.companies] : [],
          matches: Array.isArray(initialData?.metadata?.matches) ? [...initialData.metadata.matches] : [],
          contacts: Array.isArray(initialData?.metadata?.contacts) ? [...initialData.metadata.contacts] : []
        });

        // Auto-add current user to linked users for new events
        const initialLinkedUsers = Array.isArray(initialData?.metadata?.linkedUsers) ? [...initialData.metadata.linkedUsers] : [];
        if (currentUserId && !initialLinkedUsers.includes(currentUserId)) {
          setLinkedUsers([currentUserId, ...initialLinkedUsers]);
        } else {
          setLinkedUsers(initialLinkedUsers);
        }
      }
      
      setActiveTab('general');
      setMobileTab('general');
    }
  }, [isOpen, initialDate, defaultDuration, defaultColor, isEditing, initialData, currentUserId]);

  // Load matches for search
  useEffect(() => {
    if (!isOpen) return;
    const loadMatches = async () => {
      try {
        const { data, error } = await supabase
          .from('matches')
          .select(`
            id,
            candidates(name),
            jobs(title, companies(name))
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const formattedMatches = (data || []).map((match: any) => ({
          id: match.id,
          candidate_name: match.candidates?.name || 'Unknown Candidate',
          job_title: match.jobs?.title || 'Unknown Job',
          company_name: match.jobs?.companies?.name || 'Unknown Company'
        }));

        setMatches(formattedMatches);
      } catch (error) {
        console.error('Error loading matches:', error);
      }
    };

    loadMatches();
  }, [isOpen]);

  // Load jobs for search
  useEffect(() => {
    if (!isOpen) return;
    const loadJobs = async () => {
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select(`
            id,
            title,
            companies(name)
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const formattedJobs = (data || []).map((job: any) => ({
          id: job.id,
          title: job.title || 'Unknown Job',
          company_name: job.companies?.name || 'Unknown Company'
        }));

        setJobs(formattedJobs);
      } catch (error) {
        console.error('Error loading jobs:', error);
      }
    };

    loadJobs();
  }, [isOpen]);

  // Load companies for search
  useEffect(() => {
    if (!isOpen) return;
    const loadCompanies = async () => {
      try {
        const { data, error } = await supabase
          .from('companies')
          .select(`
            id,
            name,
            city,
            country
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const formattedCompanies = (data || []).map((company: any) => ({
          id: company.id,
          name: company.name || 'Unknown Company',
          city: company.city || '',
          country: company.country || ''
        }));

        setCompanies(formattedCompanies);
      } catch (error) {
        console.error('Error loading companies:', error);
      }
    };

    loadCompanies();
  }, [isOpen]);

  // Load contacts for search
  useEffect(() => {
    if (!isOpen) return;
    const loadContacts = async () => {
      try {
        const { data, error } = await supabase
          .from('contacts')
          .select(`
            id,
            first_name,
            last_name,
            email
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;

        setContacts(data || []);
      } catch (error) {
        console.error('Error loading contacts:', error);
      }
    };

    loadContacts();
  }, [isOpen]);

  // Load previous external emails for autocomplete
  useEffect(() => {
    if (!isOpen) return;
    const loadPreviousEmails = async () => {
      try {
        const { data, error } = await supabase
          .from('event_participants')
          .select('email, display_name')
          .is('contact_id', null)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const uniqueEmails = Array.from(
          new Map(data?.map(item => [item.email, item]) || []).values()
        );

        setPreviousExternalEmails(uniqueEmails);
      } catch (error) {
        console.error('Error loading previous external emails:', error);
      }
    };

    loadPreviousEmails();
  }, [isOpen]);

  // Jobs search functionality
  const [jobs, setJobs] = useState<Array<{id: string, title: string, company_name: string}>>([]);
  
  // Companies search functionality
  const [companies, setCompanies] = useState<Array<{id: string, name: string, city: string, country: string}>>([]);
  
  // Matches search functionality
  const [matches, setMatches] = useState<Array<{id: string, candidate_name: string, job_title: string, company_name: string}>>([]);

  const handleCandidateSearch = (query: string) => {
    setCandidateSearchQuery(query);
    if (!query.trim()) {
      setCandidateSearchResults([]);
      return;
    }
    
    setIsCandidateSearching(true);
    const filtered = candidates?.filter(candidate => matchesCandidateSearch(candidate, query)) || [];
    
    setCandidateSearchResults(filtered.slice(0, 5).map(c => ({
      id: c.id,
      name: c.name || c.email
    })));
    setIsCandidateSearching(false);
  };

  const handleAddCandidate = (candidate: {id: string, name: string}) => {
    if (!relations.candidates.includes(candidate.id)) {
      setRelations(prev => ({
        ...prev,
        candidates: [...prev.candidates, candidate.id]
      }));
    }
    setCandidateSearchQuery('');
    setCandidateSearchResults([]);
  };

  const handleRemoveCandidate = (candidateId: string) => {
    setRelations(prev => ({
      ...prev,
      candidates: prev.candidates.filter(id => id !== candidateId)
    }));
  };

  const handleMatchSearch = (query: string) => {
    setMatchSearchQuery(query);
    if (!query.trim()) {
      setMatchSearchResults([]);
      return;
    }
    
    setIsMatchSearching(true);
    const filtered = matches.filter(match => {
      const searchText = `${match.candidate_name} ${match.job_title} ${match.company_name}`.toLowerCase();
      const searchWords = query.toLowerCase().trim().split(/\s+/);
      return searchWords.every(word => searchText.includes(word));
    }).slice(0, 5);
    
    setMatchSearchResults(filtered.map(m => ({
      id: m.id,
      name: `${m.candidate_name} - ${m.job_title} (${m.company_name})`
    })));
    setIsMatchSearching(false);
  };

  const handleAddMatch = (match: {id: string, name: string}) => {
    if (!relations.matches.includes(match.id)) {
      setRelations(prev => ({
        ...prev,
        matches: [...prev.matches, match.id]
      }));
    }
    setMatchSearchQuery('');
    setMatchSearchResults([]);
  };

  const handleRemoveMatch = (matchId: string) => {
    setRelations(prev => ({
      ...prev,
      matches: prev.matches.filter(id => id !== matchId)
    }));
  };

  const handleJobSearch = (query: string) => {
    setJobSearchQuery(query);
    if (!query.trim()) {
      setJobSearchResults([]);
      return;
    }
    
    setIsJobSearching(true);
    const filtered = jobs.filter(job => {
      const searchText = `${job.title} ${job.company_name}`.toLowerCase();
      const searchWords = query.toLowerCase().trim().split(/\s+/);
      return searchWords.every(word => searchText.includes(word));
    }).slice(0, 5);
    
    setJobSearchResults(filtered.map(j => ({
      id: j.id,
      name: `${j.title} (${j.company_name})`
    })));
    setIsJobSearching(false);
  };

  const handleAddJob = (job: {id: string, name: string}) => {
    if (!relations.offers.includes(job.id)) {
      setRelations(prev => ({
        ...prev,
        offers: [...prev.offers, job.id]
      }));
    }
    setJobSearchQuery('');
    setJobSearchResults([]);
  };

  const handleRemoveJob = (jobId: string) => {
    setRelations(prev => ({
      ...prev,
      offers: prev.offers.filter(id => id !== jobId)
    }));
  };

  const handleCompanySearch = (query: string) => {
    setCompanySearchQuery(query);
    if (!query.trim()) {
      setCompanySearchResults([]);
      return;
    }
    
    setIsCompanySearching(true);
    const filtered = companies.filter(company => {
      const searchText = `${company.name} ${company.city} ${company.country}`.toLowerCase();
      const searchWords = query.toLowerCase().trim().split(/\s+/);
      return searchWords.every(word => searchText.includes(word));
    }).slice(0, 5);
    
    setCompanySearchResults(filtered.map(c => ({
      id: c.id,
      name: c.city && c.country ? `${c.name} (${c.city}, ${c.country})` : c.name
    })));
    setIsCompanySearching(false);
  };

  const handleAddCompany = (company: {id: string, name: string}) => {
    if (!relations.companies.includes(company.id)) {
      setRelations(prev => ({
        ...prev,
        companies: [...prev.companies, company.id]
      }));
    }
    setCompanySearchQuery('');
    setCompanySearchResults([]);
  };

  const handleRemoveCompany = (companyId: string) => {
    setRelations(prev => ({
      ...prev,
      companies: prev.companies.filter(id => id !== companyId)
    }));
  };

  const handleContactSearch = (query: string) => {
    setContactSearchQuery(query);
    if (!query.trim()) {
      setContactSearchResults([]);
      return;
    }

    setIsContactSearching(true);
    const filtered = contacts.filter(contact => {
      const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
      const searchText = `${fullName} ${contact.email}`.toLowerCase();
      const searchWords = query.toLowerCase().trim().split(/\s+/);
      return searchWords.every(word => searchText.includes(word));
    }).slice(0, 5);

    setContactSearchResults(filtered.map(c => ({
      id: c.id,
      name: `${c.first_name} ${c.last_name} (${c.email})`
    })));
    setIsContactSearching(false);
  };

  const handleAddContact = (contact: {id: string, name: string}) => {
    if (!selectedContacts.includes(contact.id)) {
      setSelectedContacts([...selectedContacts, contact.id]);
    }
    setContactSearchQuery('');
    setContactSearchResults([]);
  };

  const handleRemoveContact = (contactId: string) => {
    setSelectedContacts(selectedContacts.filter(id => id !== contactId));
  };

  const handleEmailInput = (index: number, value: string) => {
    const updated = [...externalEmails];
    updated[index].email = value;
    setExternalEmails(updated);

    // Filter suggestions based on input
    if (value.trim().length > 0) {
      const filtered = previousExternalEmails.filter(prev =>
        prev.email.toLowerCase().includes(value.toLowerCase()) &&
        !externalEmails.some(existing => existing.email === prev.email)
      );
      setEmailSuggestions(filtered);
      setActiveEmailIndex(index);
    } else {
      setEmailSuggestions([]);
      setActiveEmailIndex(-1);
    }
  };

  const handleSelectEmailSuggestion = (index: number, suggestion: {email: string, display_name: string | null}) => {
    const updated = [...externalEmails];
    updated[index].email = suggestion.email;
    updated[index].display_name = suggestion.display_name || '';
    setExternalEmails(updated);
    setEmailSuggestions([]);
    setActiveEmailIndex(-1);
  };

  const handleOnlineToggle = (checked: boolean) => {
    setIsOnline(checked);
    if (!checked) {
      // When toggling off, clear the URL
      setFormData(prev => ({ ...prev, url: '' }));
    }
    // When toggling on, URL will be populated by Unipile integration
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      return;
    }

    try {
      // For all-day events, convert to UTC midnight to preserve the date
      // For non-all-day events, use the local time converted to UTC
      const getISOString = (date: Date, isEndDate: boolean = false) => {
        if (formData.all_day) {
          const utcDate = new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
          ));
          // For all-day events, end date should be the start of the next day
          if (isEndDate)
            utcDate.setUTCDate(utcDate.getUTCDate() + 1);
          return utcDate.toISOString();
        }
        return date.toISOString();
      };

      let eventId: string;

      if (isEditing && initialData) {
        eventId = initialData.id;

        // First, update current user's response status if they're a participant
        if (currentUserId && linkedUsers.includes(currentUserId)) {
          const { error: statusError } = await supabase
            .from('event_users')
            .update({
              response_status: currentUserResponseStatus,
              responded_at: new Date().toISOString()
            })
            .eq('event_id', eventId)
            .eq('user_id', currentUserId);

          if (statusError) {
            console.error('Error updating response status:', statusError);
          }
        }

        // Check if linkedUsers have actually changed
        const currentLinkedUserIds = dbLinkedUsers.map((lu: any) => lu.user_id).sort();
        const newLinkedUserIds = [...linkedUsers].sort();
        const linkedUsersChanged = JSON.stringify(currentLinkedUserIds) !== JSON.stringify(newLinkedUserIds);

        // If linkedUsers changed, we need to preserve response statuses
        let savedResponseStatuses: Record<string, any> = {};
        if (linkedUsersChanged) {
          // Fetch current response statuses before update
          const { data: currentStatuses } = await supabase
            .from('event_users')
            .select('user_id, response_status, responded_at, is_organizer')
            .eq('event_id', eventId);

          if (currentStatuses) {
            currentStatuses.forEach(status => {
              savedResponseStatuses[status.user_id] = {
                response_status: status.response_status,
                responded_at: status.responded_at,
                is_organizer: status.is_organizer
              };
            });
          }
        }

        // Prepare participant data - only include linkedUsers if they changed
        const participantData = {
          linkedUsers: linkedUsersChanged ? (linkedUsers.length > 0 ? linkedUsers : undefined) : undefined,
          contact_ids: selectedContacts.length > 0 ? selectedContacts : undefined,
          candidate_ids: relations.candidates.length > 0 ? relations.candidates : undefined,
          external_emails: externalEmails.length > 0 ? externalEmails : undefined,
          timezone: userTimezone,
        };

        // Update existing event
        await updateEventMutation.mutateAsync({
          id: initialData.id,
          title: formData.title,
          description: formData.description || undefined,
          start_time: getISOString(formData.startDate),
          end_time: getISOString(formData.endDate, true),
          color: formData.color,
          location: formData.location || undefined,
          url: formData.url || undefined,
          priority: formData.priority,
          all_day: formData.all_day,
          conferencing: isOnline ? {} : undefined,
          relations,
          ...participantData
        });

        // If linkedUsers changed, restore response statuses for all users
        if (linkedUsersChanged && Object.keys(savedResponseStatuses).length > 0) {
          for (const userId of linkedUsers) {
            if (savedResponseStatuses[userId]) {
              await supabase
                .from('event_users')
                .update({
                  response_status: userId === currentUserId ? currentUserResponseStatus : savedResponseStatuses[userId].response_status,
                  responded_at: userId === currentUserId ? new Date().toISOString() : savedResponseStatuses[userId].responded_at,
                  is_organizer: savedResponseStatuses[userId].is_organizer
                })
                .eq('event_id', eventId)
                .eq('user_id', userId);
            } else if (userId === currentUserId) {
              // New user added (current user), set their response status
              await supabase
                .from('event_users')
                .update({
                  response_status: currentUserResponseStatus,
                  responded_at: new Date().toISOString()
                })
                .eq('event_id', eventId)
                .eq('user_id', userId);
            }
          }
        }
      } else {
        // Prepare participant data for new event
        const participantData = {
          linkedUsers: linkedUsers.length > 0 ? linkedUsers : undefined,
          contact_ids: selectedContacts.length > 0 ? selectedContacts : undefined,
          candidate_ids: relations.candidates.length > 0 ? relations.candidates : undefined,
          external_emails: externalEmails.length > 0 ? externalEmails : undefined,
          timezone: userTimezone,
        };

        // Create new event
        const newEvent = await createEventMutation.mutateAsync({
          title: formData.title,
          description: formData.description || undefined,
          start_time: getISOString(formData.startDate),
          end_time: getISOString(formData.endDate, true),
          color: formData.color,
          location: formData.location || undefined,
          url: formData.url || undefined,
          priority: formData.priority,
          visibility: formData.visibility,
          all_day: formData.all_day,
          conferencing: isOnline ? {} : undefined,
          relations,
          ...participantData
        });
        eventId = newEvent.id;

        // Set current user's response status for new event
        if (currentUserId && linkedUsers.includes(currentUserId)) {
          await supabase
            .from('event_users')
            .update({
              response_status: currentUserResponseStatus,
              responded_at: new Date().toISOString()
            })
            .eq('event_id', eventId)
            .eq('user_id', currentUserId);
        }
      }

      onSave?.();
      onClose();
    } catch (error) {
      console.error('Failed to save event:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      title: "",
      description: "",
      color: defaultColor,
      location: "",
      url: "",
      priority: "medium",
      visibility: "private",
      all_day: false,
      startDate: new Date(),
      endDate: addMinutes(new Date(), defaultDuration)
    });
    setRelations({
      candidates: [],
      offers: [],
      companies: [],
      matches: [],
      contacts: []
    });
    setActiveTab('general');
    setMobileTab('general');
    setCandidateSearchQuery('');
    setCandidateSearchResults([]);
    setMatchSearchQuery('');
    setMatchSearchResults([]);
    setJobSearchQuery('');
    setJobSearchResults([]);
    setCompanySearchQuery('');
    setCompanySearchResults([]);
    setLinkedUsers([]);
    setSelectedContacts([]);
    setExternalEmails([]);
    setContactSearchQuery('');
    setContactSearchResults([]);
    setCurrentUserResponseStatus('yes');
    setIsOnline(false);
    onClose();
  };

  const handleStartDateChange = (date: Date) => {
    setFormData(prev => ({
      ...prev,
      startDate: date,
      endDate: prev.all_day ? date : addMinutes(date, defaultDuration)
    }));
  };

  const handleEndDateChange = (date: Date) => {
    setFormData(prev => ({
      ...prev,
      endDate: date
    }));
  };

  const handleAllDayChange = (checked: boolean) => {
    setFormData(prev => {
      const startDate = new Date(prev.startDate);
      const endDate = new Date(prev.endDate);
      
      if (checked) {
        // Set to all day: start at 00:00, end at 23:59
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 0, 0);
      } else {
        // Set to default duration from start time
        endDate.setTime(startDate.getTime() + (defaultDuration * 60 * 1000));
      }
      
      return {
        ...prev,
        all_day: checked,
        startDate,
        endDate
      };
    });
  };

  const handleAddCandidateManual = () => {
    setIsAddCandidateSelectionOpen(false);
    setCandidatePrefillData(null);
    setIsAddCandidateFormOpen(true);
  };

  const handleAddCandidateCVUpload = (formData: CandidateFormData) => {
    setIsAddCandidateSelectionOpen(false);
    setCandidatePrefillData(formData);
    setIsAddCandidateFormOpen(true);
  };

  const handleCandidateAdded = (_newCandidate: Candidate) => {
    setIsAddCandidateFormOpen(false);
    setCandidatePrefillData(null);
  };

  const handleCompanySubmit = async (companyData: any) => {
    try {
      const success = await addCompany(companyData);
      if (success) {
        setIsAddCompanyOpen(false);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleCreateJob = async (jobData: any) => {
    try {
      const jobId = await addJob(jobData);
      if (jobId) {
        setIsAddJobOpen(false);
        return jobId;
      }
      return undefined;
    } catch {
      return undefined;
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-2 sm:p-4">
      <div className={cn(
        "bg-white dark:bg-gray-800 rounded-sm shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col animate-slide-in border border-gray-200 dark:border-gray-700 overflow-hidden transition-opacity duration-300",
        isDeleting && "opacity-60 pointer-events-none"
      )}>
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 dark:from-gray-800/50 to-white dark:to-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-sm flex items-center justify-center shadow-sm">
              <CalendarDays className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {isEditing ? t("calendar.editEvent", "Edit Event") : t("calendar.createEvent", "Create Event")}
                </h2>
                {isEditing && initialData?.metadata?.sync_status && (
                  <SyncStatusBadge
                    status={initialData.metadata.sync_status}
                    syncError={initialData.metadata.sync_error}
                    showLabel={true}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {customHeaderActions}
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* Mobile Tab Navigation */}
          <div className="xl:hidden border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
            <div className="flex">
              <button
                type="button"
                onClick={() => setMobileTab('general')}
                className={cn(
                  "flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                  mobileTab === 'general'
                    ? "border-blue-500 text-blue-500 bg-blue-50"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                <div className="flex items-center justify-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  					{isEditing ? t("calendar.saveEvent", "Save") : t("calendar.createEvent", "Create Event")}
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMobileTab('relations')}
                className={cn(
                  "flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                  mobileTab === 'relations'
                    ? "border-blue-500 text-blue-500 bg-blue-50"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                <div className="flex items-center justify-center gap-2">
                  <Link className="h-4 w-4" />
                  {t("calendar.relations.title", "Relations")}
                </div>
              </button>
            </div>
          </div>

          <div className="flex flex-col xl:flex-row flex-1 min-h-0 overflow-hidden">
            {/* Main Content - Left Side */}
            <div className={cn(
              "flex-1 border-r border-gray-200 dark:border-gray-700 min-h-0",
              "xl:block",
              mobileTab === 'general' ? "block" : "hidden"
            )}>
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'general' | 'custom-fields')} className="w-full h-full flex flex-col">
                {fields.length > 0 && (
                  <TabsList className="mb-4">
                    <TabsTrigger value="general">
                      <CalendarDays className="h-4 w-4 mr-2" />
                      {t("calendar.tabs.general", "General")}
                    </TabsTrigger>
                  </TabsList>
                )}
                
                <TabsContent value="general" className="flex-1 overflow-y-auto">
                  <div className="p-4 space-y-5">
                                        {/* Title and Color Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                      <div className="sm:col-span-4 space-y-2">
                        <Label htmlFor="title" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {t("calendar.form.title", "Title")} *
                        </Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          placeholder={t("calendar.form.titlePlaceholder", "Enter event title")}
                          className="h-11 text-base"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="color" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {t("calendar.form.color", "Color")}
                        </Label>
                        <Select value={formData.color} onValueChange={(value) => setFormData({ ...formData, color: value })}>
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {EVENT_COLORS.map((color) => (
                              <SelectItem key={color.value} value={color.value}>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-4 h-4 rounded-sm" 
                                    style={{ backgroundColor: color.value }}
                                  />
                                  {color.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Description Field - Full Width */}
                    <div className="space-y-2">
                      <Label htmlFor="description" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {t("calendar.form.description", "Description")}
                      </Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder={t("calendar.form.descriptionPlaceholder", "Enter event description (optional)")}
                        className="min-h-[100px] text-base resize-none"
                        rows={4}
                      />
                    </div>

                    {/* Online Toggle */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-sm border border-blue-200">
                        <div className="flex items-center gap-2">
                          <Video className="h-4 w-4 text-blue-500" />
                          <Label htmlFor="online-toggle" className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer">
                            {t("calendar.form.onlineMeeting", "Online Meeting")}
                          </Label>
                        </div>
                        <Switch
                          id="online-toggle"
                          checked={isOnline}
                          onCheckedChange={handleOnlineToggle}
                        />
                      </div>
                    </div>

                    {/* Location and URL Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="location" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {t("calendar.form.location", "Location")}
                        </Label>
                        <Input
                          id="location"
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          placeholder={t("calendar.form.locationPlaceholder", "Enter event location (optional)")}
                          className="h-11 text-base"
                          disabled={isOnline}
                        />
                      </div>
                      {isOnline && (
                        <div className="space-y-2">
                          <Label htmlFor="url" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t("calendar.form.url", "Meeting URL")}
                          </Label>
                          <Input
                            id="url"
                            value={formData.url}
                            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                            placeholder={isEditing ? t("calendar.form.urlPlaceholder", "Meeting URL") : "Will be generated automatically"}
                            className="h-11 text-base bg-gray-50 dark:bg-gray-800/50"
                            readOnly={!isEditing}
                          />
                          {!isEditing && (
                            <p className="text-xs text-muted-foreground">
                              Meeting URL will be generated automatically when synced
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Your Response Status and Priority Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="response_status" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {t("calendar.form.yourResponse", "Your Response")}
                        </Label>
                        <Select value={currentUserResponseStatus} onValueChange={(value) => setCurrentUserResponseStatus(value as 'yes' | 'no' | 'maybe' | 'noreply')}>
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {USER_RESPONSE_STATUSES.map((status) => (
                              <SelectItem key={status.value} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="priority" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {t("calendar.form.priority", "Priority")}
                        </Label>
                        <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value as 'low' | 'medium' | 'high' | 'urgent' })}>
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {EVENT_PRIORITIES.map((priority) => (
                              <SelectItem key={priority.value} value={priority.value}>
                                {priority.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Date and Time Row with All Day Checkbox */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t("calendar.form.startDate", "Start Date")}
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal h-11",
                                  !formData.startDate && "text-gray-500 dark:text-gray-400"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formData.startDate ? formatCalendarDate(formData.startDate, 'date', formData.all_day) : t("calendar.form.selectStartDate", "Select date")}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={formData.startDate}
                                onSelect={(date) => date && handleStartDateChange(date)}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t("calendar.form.startTime", "Start Time")}
                          </Label>
                          <Input
                            type="time"
                            value={formData.startDate ? `${String(formData.startDate.getHours()).padStart(2, '0')}:${String(formData.startDate.getMinutes()).padStart(2, '0')}` : ''}
                            onChange={(e) => {
                              const [hours, minutes] = e.target.value.split(':');
                              const newDate = new Date(formData.startDate);
                              newDate.setHours(parseInt(hours), parseInt(minutes));
                              handleStartDateChange(newDate);
                            }}
                            className="h-11"
                            disabled={formData.all_day}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t("calendar.form.endDate", "End Date")}
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal h-11",
                                  !formData.endDate && "text-gray-500 dark:text-gray-400"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formData.endDate ? formatCalendarDate(formData.endDate, 'date', formData.all_day) : t("calendar.form.selectEndDate", "Select date")}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={formData.endDate}
                                onSelect={(date) => date && handleEndDateChange(date)}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t("calendar.form.endTime", "End Time")}
                          </Label>
                          <Input
                            type="time"
                            value={formData.endDate ? `${String(formData.endDate.getHours()).padStart(2, '0')}:${String(formData.endDate.getMinutes()).padStart(2, '0')}` : ''}
                            onChange={(e) => {
                              const [hours, minutes] = e.target.value.split(':');
                              const newDate = new Date(formData.endDate);
                              newDate.setHours(parseInt(hours), parseInt(minutes));
                              handleEndDateChange(newDate);
                            }}
                            className="h-11"
                            disabled={formData.all_day}
                          />
                        </div>
                      </div>

                      {/* All Day Checkbox */}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="all_day"
                          checked={formData.all_day}
                          onCheckedChange={handleAllDayChange}
                        />
                        <Label htmlFor="all_day" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {t("calendar.form.allDay", "All Day")}
                        </Label>
                      </div>
                    </div>


                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Relations Sidebar - Right Side */}
            <div className={cn(
              "w-full xl:w-80 border-t xl:border-l xl:border-t-0 bg-gradient-to-b from-gray-50 dark:from-gray-800/50 to-white dark:to-gray-800 min-h-0 flex flex-col",
              "xl:block",
              mobileTab === 'relations' ? "block" : "hidden"
            )}>
              <div className="p-4 space-y-3 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 200px)' }}>

                {/* Linked Users */}
                <div className="space-y-2 p-2.5 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-purple-100 rounded-sm flex items-center justify-center">
                        <Users className="h-3 w-3 text-purple-600" />
                      </div>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t("calendar.relations.linkedUsers", "Linked Users")}</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Select onValueChange={(userId) => {
                      if (!linkedUsers.includes(userId)) {
                        setLinkedUsers([...linkedUsers, userId]);
                      }
                    }}>
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder={t("calendar.form.addUser", "Add user...")} />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => !linkedUsers.includes(u.id)).map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {linkedUsers.length > 0 ? (
                      <div className="space-y-1">
                        {linkedUsers.map(userId => {
                          const user = users.find(u => u.id === userId);
                          if (!user) return null;
                          const responseStatus = userResponseStatus[userId] || 'noreply';
                          return (
                            <div key={userId} className="flex items-center justify-between gap-2 p-2 text-xs bg-purple-50 rounded-sm">
                              <span className="truncate flex-1">{user.first_name} {user.last_name}</span>
                              {isEditing && (
                                <Badge className={`${getResponseStatusColor(responseStatus)} text-[10px] px-1.5 py-0 flex-shrink-0`}>
                                  {getResponseStatusLabel(responseStatus)}
                                </Badge>
                              )}
                              <button
                                type="button"
                                onClick={() => setLinkedUsers(linkedUsers.filter(id => id !== userId))}
                                className="text-purple-600 hover:text-purple-800 transition-colors flex-shrink-0"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">{t("calendar.relations.noLinkedUsers", "No linked users")}</p>
                    )}
                  </div>
                </div>

                {/* Contacts (for External Participants) */}
                <div className="space-y-2 p-2.5 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-red-100 rounded-sm flex items-center justify-center">
                        <Users className="h-3 w-3 text-red-600" />
                      </div>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t("calendar.relations.linkedContacts", "Contacts")}</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                      <Input
                        value={contactSearchQuery}
                        onChange={(e) => handleContactSearch(e.target.value)}
                        placeholder={t("calendar.relations.searchContacts", "Search contacts...")}
                        className="h-7 text-xs pl-7"
                      />
                    </div>
                    {contactSearchQuery && contactSearchResults.length > 0 && (
                      <div className="space-y-1 max-h-32 overflow-y-auto border rounded-sm p-1">
                        {contactSearchResults.map((contact) => (
                          <div
                            key={contact.id}
                            onClick={() => handleAddContact(contact)}
                            className="flex items-center justify-between p-1.5 text-xs bg-red-50 hover:bg-red-100 rounded-sm cursor-pointer transition-colors"
                          >
                            <span className="truncate flex-1">{contact.name}</span>
                            <Plus className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0 ml-2" />
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedContacts.length > 0 ? (
                      <div className="space-y-1">
                        {selectedContacts.map((contactId) => {
                          const contact = contacts.find(c => c.id === contactId);
                          if (!contact) return null;
                          const responseStatus = contactResponseStatus[contactId] || 'noreply';
                          return (
                            <div key={contactId} className="flex items-center justify-between gap-2 p-2 text-xs bg-red-50 rounded-sm">
                              <span className="truncate flex-1">{contact.first_name} {contact.last_name}</span>
                              {isEditing && (
                                <Badge className={`${getResponseStatusColor(responseStatus)} text-[10px] px-1.5 py-0 flex-shrink-0`}>
                                  {getResponseStatusLabel(responseStatus)}
                                </Badge>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveContact(contactId)}
                                className="text-red-600 hover:text-red-800 transition-colors flex-shrink-0"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-1.5 bg-gray-50 dark:bg-gray-800/50 rounded-sm">{t("calendar.relations.noContactsLinked", "No contacts linked")}</p>
                    )}
                  </div>
                </div>

                {/* External Emails (guests not in the system) */}
                <div className="space-y-2 p-2.5 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-teal-100 rounded-sm flex items-center justify-center">
                        <Users className="h-3 w-3 text-teal-600" />
                      </div>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">External Guests</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setExternalEmails([...externalEmails, { email: '', display_name: '', attendance_type: 'required' }])}
                      className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {externalEmails.length > 0 ? (
                      externalEmails.map((attendee, index) => {
                        const responseStatus = externalEmailResponseStatus[attendee.email] || 'noreply';
                        return (
                          <div key={index} className="space-y-1 p-2 bg-teal-50 rounded-sm">
                            {isEditing && attendee.email && (
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-gray-600 dark:text-gray-300 font-medium">Response Status:</span>
                                <Badge className={`${getResponseStatusColor(responseStatus)} text-[10px] px-1.5 py-0`}>
                                  {getResponseStatusLabel(responseStatus)}
                                </Badge>
                              </div>
                            )}
                            <div className="flex gap-1 relative">
                              <div className="flex-1 relative">
                                <Input
                                  type="email"
                                  placeholder="email@example.com"
                                  value={attendee.email}
                                  onChange={(e) => handleEmailInput(index, e.target.value)}
                                  onBlur={() => {
                                    setTimeout(() => {
                                      setEmailSuggestions([]);
                                      setActiveEmailIndex(-1);
                                    }, 200);
                                  }}
                                  className="h-6 text-xs"
                                />
                              {emailSuggestions.length > 0 && activeEmailIndex === index && (
                                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm shadow-lg max-h-48 overflow-y-auto">
                                  {emailSuggestions.map((suggestion, suggestionIndex) => (
                                    <button
                                      key={suggestionIndex}
                                      type="button"
                                      onClick={() => handleSelectEmailSuggestion(index, suggestion)}
                                      className="w-full text-left px-3 py-2 hover:bg-teal-50 dark:hover:bg-teal-950/30 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                    >
                                      <div className="text-xs font-medium text-gray-900 dark:text-gray-100">{suggestion.email}</div>
                                      {suggestion.display_name && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{suggestion.display_name}</div>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setExternalEmails(externalEmails.filter((_, i) => i !== index))}
                              className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 transition-colors px-1"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                          <Input
                            placeholder="Name (optional)"
                            value={attendee.display_name}
                            onChange={(e) => {
                              const updated = [...externalEmails];
                              updated[index].display_name = e.target.value;
                              setExternalEmails(updated);
                            }}
                            className="h-6 text-xs"
                          />
                          <Select
                            value={attendee.attendance_type}
                            onValueChange={(value: 'required' | 'optional' | 'resource') => {
                              const updated = [...externalEmails];
                              updated[index].attendance_type = value;
                              setExternalEmails(updated);
                            }}
                          >
                            <SelectTrigger className="h-6 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="required">Required</SelectItem>
                              <SelectItem value="optional">Optional</SelectItem>
                              <SelectItem value="resource">Resource</SelectItem>
                            </SelectContent>
                          </Select>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-1.5 bg-gray-50 dark:bg-gray-800/50 rounded-sm">
                        No external guests
                      </div>
                    )}
                  </div>
                </div>

                {/* Candidates */}
                <EntitySearchPopover
                  entityType="candidate"
                  searchQuery={candidateSearchQuery}
                  onSearchChange={handleCandidateSearch}
                  searchResults={candidateSearchResults}
                  isSearching={isCandidateSearching}
                  selectedIds={relations.candidates}
                  onAddItem={handleAddCandidate}
                  onRemoveItem={handleRemoveCandidate}
                  placeholder={t("calendar.relations.searchCandidates", "Search candidates...")}
                  icon={<Users className="h-3 w-3" />}
                  iconBgColor="bg-blue-100"
                  iconColor="text-blue-500"
                  resultBgColor="bg-blue-50"
                  resultBgColorDark="dark:bg-blue-900/30"
                  resultHoverColor="hover:bg-blue-100"
                  resultHoverColorDark="dark:hover:bg-blue-900/50"
                  noItemsText={t("calendar.relations.noCandidatesLinked", "No candidates linked")}
                  allAvailableItems={candidates?.map(c => ({ id: c.id, name: c.name || c.email })) || []}
                  onCreateNew={() => setIsAddCandidateSelectionOpen(true)}
                />

                {/* Jobs */}
                <EntitySearchPopover
                  entityType="job"
                  searchQuery={jobSearchQuery}
                  onSearchChange={handleJobSearch}
                  searchResults={jobSearchResults}
                  isSearching={isJobSearching}
                  selectedIds={relations.offers}
                  onAddItem={handleAddJob}
                  onRemoveItem={handleRemoveJob}
                  placeholder={t("calendar.relations.searchJobs", "Search jobs...")}
                  icon={<ClipboardList className="h-3 w-3" />}
                  iconBgColor="bg-green-100"
                  iconBgColorDark="dark:bg-green-900"
                  iconColor="text-green-600"
                  iconColorDark="dark:text-green-400"
                  resultBgColor="bg-green-50"
                  resultBgColorDark="dark:bg-green-900/30"
                  resultHoverColor="hover:bg-green-100"
                  resultHoverColorDark="dark:hover:bg-green-900/50"
                  noItemsText={t("calendar.relations.noJobsLinked", "No jobs linked")}
                  allAvailableItems={jobs.map(j => ({ id: j.id, name: `${j.title} (${j.company_name})` }))}
                  onCreateNew={() => setIsAddJobOpen(true)}
                />

                {/* Companies */}
                <EntitySearchPopover
                  entityType="company"
                  searchQuery={companySearchQuery}
                  onSearchChange={handleCompanySearch}
                  searchResults={companySearchResults}
                  isSearching={isCompanySearching}
                  selectedIds={relations.companies}
                  onAddItem={handleAddCompany}
                  onRemoveItem={handleRemoveCompany}
                  placeholder={t("calendar.relations.searchCompanies", "Search companies...")}
                  icon={<Building className="h-3 w-3" />}
                  iconBgColor="bg-orange-100"
                  iconBgColorDark="dark:bg-orange-900"
                  iconColor="text-orange-600"
                  iconColorDark="dark:text-orange-400"
                  resultBgColor="bg-orange-50"
                  resultBgColorDark="dark:bg-orange-900/30"
                  resultHoverColor="hover:bg-orange-100"
                  resultHoverColorDark="dark:hover:bg-orange-900/50"
                  noItemsText={t("calendar.relations.noCompaniesLinked", "No companies linked")}
                  allAvailableItems={companies.map(c => ({
                    id: c.id,
                    name: c.city && c.country ? `${c.name} (${c.city}, ${c.country})` : c.name
                  }))}
                  onCreateNew={() => setIsAddCompanyOpen(true)}
                />

                {/* Matches */}
                <EntitySearchPopover
                  entityType="match"
                  searchQuery={matchSearchQuery}
                  onSearchChange={handleMatchSearch}
                  searchResults={matchSearchResults}
                  isSearching={isMatchSearching}
                  selectedIds={relations.matches}
                  onAddItem={handleAddMatch}
                  onRemoveItem={handleRemoveMatch}
                  placeholder={t("calendar.relations.searchMatches", "Search matches...")}
                  icon={<Puzzle className="h-3 w-3" />}
                  iconBgColor="bg-purple-100"
                  iconBgColorDark="dark:bg-purple-900"
                  iconColor="text-purple-600"
                  iconColorDark="dark:text-purple-400"
                  resultBgColor="bg-purple-50"
                  resultBgColorDark="dark:bg-purple-900/30"
                  resultHoverColor="hover:bg-purple-100"
                  resultHoverColorDark="dark:hover:bg-purple-900/50"
                  noItemsText={t("calendar.relations.noMatchesLinked", "No matches linked")}
                  allAvailableItems={matches.map(m => ({
                    id: m.id,
                    name: `${m.candidate_name} - ${m.job_title} (${m.company_name})`
                  }))}
                />
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex flex-col sm:flex-row justify-between p-3 sm:p-4 lg:p-6 border-t border-gray-200 dark:border-gray-700 gap-2 sm:gap-3 bg-gradient-to-r from-gray-50 dark:from-gray-800/50 to-white dark:to-gray-800 flex-shrink-0">
            <div>
              {isEditing && onDelete && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:border-red-400 text-sm sm:text-base"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("calendar.deleteEvent", "Delete Event")}
                </Button>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm sm:text-base"
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                type="submit"
                disabled={!formData.title.trim()}
                className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2"
              >
                {isEditing ? t("common.save", "Save") : t("calendar.createEvent", "Create Event")}
              </Button>
            </div>
          </div>
        </form>

        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("calendar.deleteEvent", "Delete Event")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("calendar.deleteConfirm", "Are you sure you want to delete this event?")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>{t("common.cancel", "Cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  onDelete?.();
                  setShowDeleteConfirm(false);
                }}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? t("common.deleting", "Deleting...") : t("common.delete", "Delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
    <AddCandidateSelectionModal
      isOpen={isAddCandidateSelectionOpen}
      onClose={() => setIsAddCandidateSelectionOpen(false)}
      onManualCreate={handleAddCandidateManual}
      onCVUpload={handleAddCandidateCVUpload}
    />
    <AddCandidateForm
      isOpen={isAddCandidateFormOpen}
      onClose={() => { setIsAddCandidateFormOpen(false); setCandidatePrefillData(null); }}
      onCandidateAdded={handleCandidateAdded}
      initialFormData={candidatePrefillData}
    />
    <AddCompanyForm
      isOpen={isAddCompanyOpen}
      onClose={() => setIsAddCompanyOpen(false)}
      onSubmit={handleCompanySubmit}
    />
    <JobOffersAddModal
      isOpen={isAddJobOpen}
      onClose={() => setIsAddJobOpen(false)}
      onAddJob={handleCreateJob}
    />
    </>
  );
};
